import React from 'react';
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import PromptEditor from './PromptEditor.jsx';
import ResultTemplateDialog from './ResultTemplateDialog.jsx';

// Every dialog the receiver tab can open: the two that gate a save, the enlarged prompt
// and template editors, and the delete confirmation.
const WasteReceiverDialogs = ({ tab, saving }) => {
    const {
        closeColumnMigrationDialog,
        closeCompanyReassignmentDialog,
        closeDeleteDialog,
        confirmColumnMigration,
        confirmCompanyReassignment,
        deleteDialogOpen,
        deleteWasteReceiver,
        draftMode,
        draftResultTemplate,
        draftSystemPrompt,
        draftSystemPromptPlaceholders,
        isCreating,
        pendingColumnMigration,
        pendingCompanyChange,
        resultTemplateDialogOpen,
        resultTemplateFindings,
        selectedReceiver,
        setResultTemplateDialogOpen,
        setSystemPromptDialogOpen,
        systemPromptDialogOpen,
        updateDraftResultTemplate,
        updateDraftSystemPrompt,
    } = tab;

    return (
        <>
            <Dialog
                open={Boolean(pendingColumnMigration)}
                onClose={closeColumnMigrationDialog}
                className="setup-dialog"
            >
                <DialogTitle>Tilpas sorteringslisten til kolonnerne?</DialogTitle>
                <DialogContent>
                    <p>
                        Kolonneændringerne flytter alle værdier i den nye positionsbaserede liste.
                        Legacy sorting_items bevares uændret.
                    </p>
                    {pendingColumnMigration?.addedColumns?.length > 0 && (
                        <div>
                            <strong>Tilføjede kolonner</strong>
                            <ul>
                                {pendingColumnMigration.addedColumns.map((columnName) => (
                                    <li key={`added-${columnName}`}>{columnName} — tom værdi tilføjes</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {pendingColumnMigration?.removedColumns?.length > 0 && (
                        <div>
                            <strong>Fjernede kolonner</strong>
                            <ul>
                                {pendingColumnMigration.removedColumns.map((columnName) => (
                                    <li key={`removed-${columnName}`}>{columnName} — værdien fjernes</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {pendingColumnMigration?.renamedColumns?.length > 0 && (
                        <div>
                            <strong>Omdøbte kolonner</strong>
                            <ul>
                                {pendingColumnMigration.renamedColumns.map(({ from, to }) => (
                                    <li key={`renamed-${from}-${to}`}>{from} → {to}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {pendingColumnMigration?.reordered && (
                        <p>Kolonnernes nye rækkefølge anvendes på alle positionsbaserede rækker.</p>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeColumnMigrationDialog} disabled={saving}>
                        Annuller
                    </Button>
                    <Button
                        variant="contained"
                        onClick={confirmColumnMigration}
                        disabled={saving}
                    >
                        Tilpas og gem
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={systemPromptDialogOpen && draftMode !== 'view'}
                onClose={() => setSystemPromptDialogOpen(false)}
                className="setup-dialog"
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>System prompt</DialogTitle>
                <DialogContent className="setup-prompt-dialog-content">
                    <PromptEditor
                        label="System prompt"
                        value={draftSystemPrompt}
                        onChange={updateDraftSystemPrompt}
                        disabled={saving}
                        minRows={24}
                        autoFocus
                        helperText="Ændringer gemmes sammen med resten af affaldsmodtageren, når du trykker Gem."
                    />

                    <div className="setup-prompt-placeholder-list">
                        <span className="setup-field-label">Pladsholdere</span>
                        {draftSystemPromptPlaceholders.length === 0 ? (
                            <span className="empty-inline">Ingen</span>
                        ) : (
                            draftSystemPromptPlaceholders.map((placeholder) => (
                                <Chip
                                    key={placeholder}
                                    size="small"
                                    label={placeholder}
                                    className="setup-chip"
                                />
                            ))
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => setSystemPromptDialogOpen(false)}
                    >
                        Luk
                    </Button>
                </DialogActions>
            </Dialog>

            <ResultTemplateDialog
                open={resultTemplateDialogOpen && draftMode !== 'view'}
                onClose={() => setResultTemplateDialogOpen(false)}
                // A receiver gets its Firestore id on first save, and the preview reads that
                // document, so a receiver being created has nothing to render against yet.
                wasteReceiverId={isCreating ? '' : selectedReceiver?.id || ''}
                html={draftResultTemplate.html}
                css={draftResultTemplate.css}
                onChange={updateDraftResultTemplate}
                saveFindings={resultTemplateFindings}
                disabled={saving}
            />

            <Dialog
                open={deleteDialogOpen}
                onClose={closeDeleteDialog}
                className="setup-dialog"
            >
                <DialogTitle>Slet affaldsmodtager?</DialogTitle>
                <DialogContent>
                    Dette sletter affaldsmodtageren{' '}
                    <strong>{selectedReceiver?.name || selectedReceiver?.id}</strong> og fjerner
                    tilknytningen fra alle brugere. Handlingen kan ikke fortrydes.
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} disabled={saving}>
                        Annuller
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={deleteWasteReceiver}
                        disabled={saving}
                    >
                        Slet
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(pendingCompanyChange)}
                onClose={closeCompanyReassignmentDialog}
                className="setup-dialog"
            >
                <DialogTitle>Flyt virksomhed?</DialogTitle>
                <DialogContent>
                    {pendingCompanyChange?.conflicts?.map((conflict) => (
                        <p key={conflict.companyId}>
                            <strong>{conflict.companyLabel}</strong> er allerede tilknyttet{' '}
                            <strong>{conflict.wasteReceiverLabel}</strong>. Virksomheden fjernes
                            derfra, hvis du fortsætter.
                        </p>
                    ))}
                    Vil du fortsætte?
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeCompanyReassignmentDialog} disabled={saving}>
                        Annuller
                    </Button>
                    <Button
                        variant="contained"
                        onClick={confirmCompanyReassignment}
                        disabled={saving}
                    >
                        Fortsæt
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default WasteReceiverDialogs;
